/** Cue cell content may be legacy plain text or TipTap HTML. */

const HTML_START = /^\s*</;

export function looksLikeCueHtml(content: string): boolean {
  return HTML_START.test(content);
}

export function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Plain text for empty-checks, merge previews, comments, and clipboard. */
export function cueContentPlainText(content: string): string {
  if (!content) return "";
  if (!looksLikeCueHtml(content)) return content;
  return content
    .replace(/\r\n?/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, n: string) =>
      String.fromCharCode(Number(n)),
    )
    .replace(/\n+$/u, "")
    .replace(/^\n+/u, "")
    .replace(/\n{3,}/g, "\n\n");
}

export function isCueContentEmpty(content: string): boolean {
  return cueContentPlainText(content).trim() === "";
}

/** Convert stored cell content into TipTap setContent input. */
export function contentForEditor(text: string): string | object {
  if (!text) {
    return { type: "doc", content: [{ type: "paragraph" }] };
  }
  if (looksLikeCueHtml(text)) return text;
  const paragraphs = text.split("\n");
  return {
    type: "doc",
    content: paragraphs.map((line) => ({
      type: "paragraph",
      content: line ? [{ type: "text", text: line }] : [],
    })),
  };
}

/** Drop trailing empty paragraphs TipTap leaves after the first Return. */
export function stripTrailingEmptyParagraphs(html: string): string {
  const stripped = html
    .replace(/(?:<p>(?:\s|&nbsp;|<br\b[^>]*>)*<\/p>)+$/gi, "")
    .trim();
  return stripped || "<p></p>";
}

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "s",
  "strike",
  "del",
  "u",
  "sup",
  "sub",
  "span",
]);

const SAFE_CSS_COLOR =
  /^(#([0-9a-f]{3}|[0-9a-f]{6})|rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\))$/i;

/** Extract a safe CSS color from an inline style attribute. */
export function cssColorFromStyle(
  style: string | null | undefined,
): string | undefined {
  if (!style) return undefined;
  const match = style.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  const value = match?.[1]?.trim();
  if (!value || !SAFE_CSS_COLOR.test(value)) return undefined;
  return value;
}

/** Normalize a CSS color to `#rrggbb` for color inputs and Word/RTF. */
export function cssColorToHex(
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (/^#([0-9a-f]{3})$/i.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  if (/^#([0-9a-f]{6})$/i.test(v)) return v.toLowerCase();
  const rgb = v.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (!rgb) return undefined;
  const hex = [rgb[1], rgb[2], rgb[3]]
    .map((n) =>
      Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, "0"),
    )
    .join("");
  return `#${hex}`;
}

/**
 * Empty `<p></p>` (a leading Return in a cell) collapses to 0 height in
 * print/PDF. Keep a non-breaking space so the blank line is visible.
 */
function printParagraphHtml(inner: string): string {
  const visible = inner
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, "")
    .replace(/&amp;/gi, "&")
    .trim();
  return `<p>${visible ? inner : "&nbsp;"}</p>`;
}

/** Allowlist sanitizer for print/preview of editor HTML. */
export function sanitizeCueHtml(html: string): string {
  if (!html || !looksLikeCueHtml(html)) {
    const plain = html || "";
    if (!plain) return "<p></p>";
    return plain
      .split("\n")
      .map((line) => `<p>${escapeXml(line) || "&nbsp;"}</p>`)
      .join("");
  }

  if (typeof DOMParser === "undefined") {
    return html.replace(
      /<\/?(?!\/?(?:p|br|strong|b|em|i|s|strike|del|u|sup|sub|span)\b)[^>]*>/gi,
      "",
    );
  }

  const doc = new DOMParser().parseFromString(
    `<div class="sm-rich-root">${html}</div>`,
    "text/html",
  );
  const root = doc.body.querySelector(".sm-rich-root");
  if (!root) return "<p></p>";

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeXml(node.textContent ?? "");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === "script" || tag === "style") return "";
    const inner = Array.from(el.childNodes).map(walk).join("");
    if (tag === "br") return "<br>";
    if (!ALLOWED_TAGS.has(tag)) return inner;
    if (tag === "b") return `<strong>${inner}</strong>`;
    if (tag === "i") return `<em>${inner}</em>`;
    if (tag === "strike" || tag === "del") return `<s>${inner}</s>`;
    if (tag === "span") {
      const color = cssColorFromStyle(el.getAttribute("style"));
      return color
        ? `<span style="color: ${escapeXml(color)}">${inner}</span>`
        : inner;
    }
    if (tag === "p") return printParagraphHtml(inner);
    return `<${tag}>${inner}</${tag}>`;
  };

  const out = Array.from(root.childNodes).map(walk).join("");
  return out || "<p></p>";
}

/** Join several cell contents when merging spanned rows. */
export function joinCueContents(parts: string[]): string {
  const blocks = parts
    .map((p) => p.trim())
    .filter((p) => !isCueContentEmpty(p))
    .map((p) => {
      if (looksLikeCueHtml(p)) return stripTrailingEmptyParagraphs(p);
      return p
        .split("\n")
        .map((line) => `<p>${escapeXml(line)}</p>`)
        .join("");
    });
  return blocks.join("");
}
